import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Modal, Progress, Result, Space, Spin, Typography, Descriptions, Tag, message } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';
import { InspectionRequest } from '@/services/types/inspection.types';
import aiAnalysisService, { AutomaticReportResponse } from '@/services/aiAnalysis.service';
import { AutomaticLaudoGenerator } from '../AutomaticLaudoGenerator';

const { Title, Text, Paragraph } = Typography;

interface AutomaticLaudoStatusProps {
  inspectionItemId: string;
  itemStatus: InspectionItemStatus;
  inspection: InspectionRequest;
  onReportReady?: (reportData: AutomaticReportResponse) => void;
  onRetry?: () => void;
  onPdfGenerated?: () => void;
  onRefresh?: () => void;
  onForceManual?: () => void;
  /** Quando definido, exibe o botão "Cancelar e Preencher Manualmente" durante o processamento. */
  onCancelAutomatic?: () => void;
}

type ProcessingStage = 'scraping' | 'ai_analysis' | 'completed' | 'failed';

const getStageFromStatus = (status: InspectionItemStatus): ProcessingStage => {
  switch (status) {
    case InspectionItemStatus.ProcessandoScraping:
      return 'scraping';
    case InspectionItemStatus.AguardandoAnaliseIA:
    case InspectionItemStatus.ProcessandoAnaliseIA:
      return 'ai_analysis';
    case InspectionItemStatus.LaudoAutomaticoGerado:
      return 'completed';
    case InspectionItemStatus.ScrapingFalhou:
    case InspectionItemStatus.AnaliseIAFalhou:
      return 'failed';
    default:
      return 'scraping';
  }
};

const getProgressPercent = (stage: ProcessingStage): number => {
  switch (stage) {
    case 'scraping':
      return 25;
    case 'ai_analysis':
      return 65;
    case 'completed':
      return 100;
    case 'failed':
      return 0;
    default:
      return 0;
  }
};

export const AutomaticLaudoStatus: React.FC<AutomaticLaudoStatusProps> = ({
  inspectionItemId,
  itemStatus,
  inspection,
  onReportReady,
  onRetry,
  onPdfGenerated,
  onRefresh,
  onForceManual,
  onCancelAutomatic,
}) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<AutomaticReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfGeneratedSuccess, setPdfGeneratedSuccess] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const lastStatusRef = React.useRef(itemStatus);

  const handleCancelClick = () => {
    if (!onCancelAutomatic) return;
    Modal.confirm({
      title: 'Cancelar processamento automático?',
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      content:
        'O processamento da IA será interrompido e você poderá preencher o laudo manualmente. Os dados coletados até agora serão descartados.',
      okText: 'Sim, cancelar',
      okButtonProps: { danger: true, loading: cancelling },
      cancelText: 'Continuar processando',
      onOk: async () => {
        try {
          setCancelling(true);
          await onCancelAutomatic();
        } catch (err: any) {
          message.error(err?.message || 'Erro ao cancelar processamento');
        } finally {
          setCancelling(false);
        }
      },
    });
  };

  const stage = getStageFromStatus(itemStatus);
  const isProcessing = stage === 'scraping' || stage === 'ai_analysis';
  const isFailed = stage === 'failed';
  const isCompleted = stage === 'completed';

  // Polling leve: consulta apenas o status do AI job a cada 15s
  // Só chama onRefresh (reload completo) quando o status realmente muda
  useEffect(() => {
    if (!isProcessing || !onRefresh) return;

    const interval = setInterval(async () => {
      try {
        const aiJob = await aiAnalysisService.getAiAnalysis(inspectionItemId);
        const aiStatus = aiJob?.status;

        // Determina se o status do InspectionItem deveria ter mudado
        // baseado no progresso do AI job
        // Se AI job completou ou falhou enquanto frontend ainda mostra processando, refresh
        const shouldRefresh =
          (aiJob && stage === 'scraping') ||
          aiStatus === 'Completed' ||
          aiStatus === 'Failed';

        if (shouldRefresh) {
          onRefresh();
        }
      } catch {
        // Silenciar erros de polling — não é crítico
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [isProcessing, inspectionItemId, stage, onRefresh]);

  const handlePdfGenerated = () => {
    setPdfGeneratedSuccess(true);
    // Pequeno delay para garantir que o backend processou a mudança de status
    setTimeout(() => {
      onPdfGenerated?.();
    }, 500);
  };

  useEffect(() => {
    if (isCompleted) {
      fetchReportData();
    }
  }, [isCompleted, inspectionItemId]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiAnalysisService.getAutomaticReportData(inspectionItemId);
      setReportData(data);
      if (onReportReady && data.reportData) {
        onReportReady(data);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados do laudo');
    } finally {
      setLoading(false);
    }
  };

  const renderProcessingStatus = () => (
    <Card bordered={false}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <RobotOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4} style={{ margin: 0 }}>
            Laudo Automático em Processamento
          </Title>
          <Paragraph type="secondary" style={{ marginTop: 8 }}>
            A inteligência artificial está analisando o imóvel
          </Paragraph>
        </div>

        <Progress
          percent={getProgressPercent(stage)}
          status="active"
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
        />

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {stage === 'scraping' ? (
              <LoadingOutlined style={{ color: '#1890ff' }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            )}
            <Text strong={stage === 'scraping'}>
              <SearchOutlined style={{ marginRight: 8 }} />
              Buscando imóveis similares na região...
            </Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {stage === 'ai_analysis' ? (
              <LoadingOutlined style={{ color: '#1890ff' }} />
            ) : stage === 'scraping' ? (
              <span style={{ width: 14 }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            )}
            <Text type={stage === 'scraping' ? 'secondary' : undefined as any} strong={stage === 'ai_analysis'}>
              <RobotOutlined style={{ marginRight: 8 }} />
              IA analisando e selecionando amostras...
            </Text>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 14 }} />
            <Text type="secondary">
              <FilePdfOutlined style={{ marginRight: 8 }} />
              Gerando laudo de avaliação...
            </Text>
          </div>
        </Space>

        <Alert
          type="info"
          message="Processamento automático"
          description="Este processo pode levar alguns minutos. Você pode acompanhar o progresso nesta tela ou voltar mais tarde."
          showIcon
        />

        {onCancelAutomatic && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={handleCancelClick}
              loading={cancelling}
            >
              Cancelar e Preencher Manualmente
            </Button>
          </div>
        )}
      </Space>
    </Card>
  );

  const renderFailedStatus = () => {
    const actions: React.ReactNode[] = [];
    if (onRetry) {
      actions.push(
        <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={onRetry}>
          Tentar Novamente
        </Button>,
      );
    }
    if (onForceManual) {
      actions.push(
        <Button key="manual" icon={<EditOutlined />} onClick={onForceManual}>
          Continuar Mesmo Assim
        </Button>,
      );
    }

    return (
      <Result
        status="error"
        title="Falha no Processamento"
        subTitle={
          itemStatus === InspectionItemStatus.ScrapingFalhou
            ? 'Não foi possível encontrar imóveis similares na região. Você pode tentar novamente ou continuar preenchendo o laudo manualmente.'
            : 'A análise de IA encontrou um problema. Você pode tentar novamente ou continuar preenchendo o laudo manualmente.'
        }
        extra={actions.length > 0 ? actions : undefined}
      />
    );
  };

  const renderCompletedStatus = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Carregando dados do laudo...</Paragraph>
        </div>
      );
    }

    if (error) {
      return (
        <Result
          status="warning"
          title="Erro ao Carregar Laudo"
          subTitle={error}
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchReportData}>
              Tentar Novamente
            </Button>
          }
        />
      );
    }

    if (!reportData?.reportData) {
      return (
        <Result
          status="info"
          title="Laudo em Preparação"
          subTitle="Os dados do laudo ainda estão sendo processados. Aguarde alguns instantes."
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchReportData}>
              Atualizar
            </Button>
          }
        />
      );
    }

    const { reportData: data } = reportData;

    return (
      <Card bordered={false}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Result
            status="success"
            title="Laudo Automático Gerado"
            subTitle="A avaliação foi concluída com sucesso pela inteligência artificial."
          />

          <Descriptions
            title="Resumo da Avaliação"
            bordered
            column={{ xs: 1, sm: 2 }}
          >
            <Descriptions.Item label="Valor Estimado" span={2}>
              <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                {data.avaliacaoFinal.valorEstimado}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Valor Mínimo">
              {data.avaliacaoFinal.valorMinimo}
            </Descriptions.Item>
            <Descriptions.Item label="Valor Máximo">
              {data.avaliacaoFinal.valorMaximo}
            </Descriptions.Item>
            <Descriptions.Item label="Valor por m²">
              {data.avaliacaoFinal.valorM2Medio}/m²
            </Descriptions.Item>
            <Descriptions.Item label="Amostras Utilizadas">
              <Tag color="blue">{data.metodologia.amostrasUtilizadas} imóveis</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Metodologia" span={2}>
              {data.metodologia.nome}
            </Descriptions.Item>
          </Descriptions>

          <Card size="small" title="Dados do Imóvel Avaliado">
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Endereço" span={2}>
                {data.dadosImovel.enderecoRua}, {data.dadosImovel.enderecoNumero}
                {data.dadosImovel.enderecoComplemento && ` - ${data.dadosImovel.enderecoComplemento}`}
              </Descriptions.Item>
              <Descriptions.Item label="Bairro">
                {data.dadosImovel.enderecoBairro}
              </Descriptions.Item>
              <Descriptions.Item label="Cidade/UF">
                {data.dadosImovel.enderecoMunicipio}/{data.dadosImovel.enderecoUf}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo">
                {data.dadosImovel.tipoImovel}
              </Descriptions.Item>
              <Descriptions.Item label="Área Privativa">
                {data.dadosImovel.areaPrivativa}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {pdfGeneratedSuccess ? (
            <Alert
              type="success"
              message="PDF gerado e salvo com sucesso!"
              description="O laudo foi salvo no sistema e está aguardando revisão do banco. A página será atualizada automaticamente."
              showIcon
              icon={<CheckCircleOutlined />}
            />
          ) : (
            <>
              <Alert
                type="success"
                message="Laudo pronto para geração do PDF"
                description="Clique no botão para gerar o PDF e salvar no sistema. A assinatura usada será a do usuário logado."
                showIcon
                icon={<CheckCircleOutlined />}
              />

              {reportData && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <AutomaticLaudoGenerator
                    inspection={inspection}
                    aiReportData={reportData}
                    onSuccess={handlePdfGenerated}
                    onError={(error) => console.error('Erro ao gerar PDF:', error)}
                  />
                </div>
              )}
            </>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <div>
      {isProcessing && renderProcessingStatus()}
      {isFailed && renderFailedStatus()}
      {isCompleted && renderCompletedStatus()}
    </div>
  );
};

export default AutomaticLaudoStatus;
